import { noop } from 'lodash';
import { useState } from 'react';
import { useTimeoutFn, useToggle, useUpdateEffect } from 'react-use';

import { Advertisement, AdvertisementGenerateCommand } from '@dreryk/egabinet-advertisement-frontend-api';
import { Visit } from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { Slot } from '@dreryk/watermelon';

import { Banner } from './Banner';
import { DrErykStaticBanner } from './DrErykStaticBanner';

const ONE_SECOND_MULTIPLIER = 1000;
const advertisementExpirationTimeInSeconds = (advertisement: Advertisement | null): number => {
   if (!advertisement) {
      return 0;
   }

   return advertisement.expirationTimeInSeconds * ONE_SECOND_MULTIPLIER;
};

type PrescriptionsAdvertisementBannerProps = {
   readonly visitId: Visit['id'];
   readonly allPrescriptionsCount: number;
};

export const PrescriptionsAdvertisementBanner = (props: PrescriptionsAdvertisementBannerProps): JSX.Element | null => {
   const [bannerError, toggleBannerError] = useToggle(false);
   const [advertisement, setAdvertisement] = useState<Advertisement | null>(null);

   const { execute: generateAdvertisement, error: generateAdvertisementError } = useCqrsCommand<Advertisement, void>(
      () => {
         const command = new AdvertisementGenerateCommand();
         command.location = 'CREATING_PRESCRIPTION';
         command.visitId = Slot.of(props.visitId);
         return command;
      },
      {
         onSuccess: setAdvertisement,
         onUnexpectedError: noop,
         background: true,
      }
   );

   useUpdateEffect(generateAdvertisement, [props.allPrescriptionsCount]);

   const [, , resetTimeout] = useTimeoutFn(generateAdvertisement, advertisementExpirationTimeInSeconds(advertisement));

   if (generateAdvertisementError || bannerError) {
      return <DrErykStaticBanner />;
   }

   if (advertisement) {
      return (
         <Banner
            key={advertisement.id}
            testId={advertisement.id}
            bannerUrl={advertisement.bannerUrl}
            targetUrl={advertisement.advertisementUrl}
            onLoad={resetTimeout}
            onError={toggleBannerError}
         />
      );
   }

   return null;
};
